import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Inbox, Mail, MailOpen, Trash2, UserCheck, UserX, Users, Shield, CheckCircle, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminInboxPage() {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/');
      return;
    }
    if (isAdmin) {
      fetchMessages();
    }
  }, [isAdmin, authLoading, navigate]);

  const fetchMessages = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/inbox`);
      setMessages(data);
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/admin/inbox/${messageId}/read`, {});
      setMessages(messages.map(m => m.id === messageId ? { ...m, status: 'read' } : m));
    } catch (err) {
      toast.error('Failed to mark as read');
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/admin/inbox/${messageId}`);
      setMessages(messages.filter(m => m.id !== messageId));
      toast.success('Message deleted');
    } catch (err) {
      toast.error('Failed to delete message');
    }
  };

  const approveUser = async (userId, messageId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/approve`, {});
      toast.success('User approved!');
      setMessages(messages.filter(m => m.id !== messageId));
    } catch (err) {
      toast.error('Failed to approve user');
    }
  };

  const declineUser = async (userId, messageId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/decline`, {});
      toast.success('User declined');
      setMessages(messages.filter(m => m.id !== messageId));
    } catch (err) {
      toast.error('Failed to decline user');
    }
  };

  const approveTeam = async (teamId, messageId) => {
    try {
      await axios.put(`${API}/admin/teams/${teamId}/approve`, {});
      toast.success('Team approved!');
      setMessages(messages.filter(m => m.id !== messageId));
    } catch (err) {
      toast.error('Failed to approve team');
    }
  };

  const declineTeam = async (teamId, messageId) => {
    try {
      await axios.put(`${API}/admin/teams/${teamId}/decline`, {});
      toast.success('Team declined');
      setMessages(messages.filter(m => m.id !== messageId));
    } catch (err) {
      toast.error('Failed to decline team');
    }
  };

  const approveClan = async (clanId, messageId) => {
    try {
      await axios.put(`${API}/admin/clans/${clanId}/approve`, {});
      toast.success('Clan approved!');
      setMessages(messages.filter(m => m.id !== messageId));
    } catch (err) {
      toast.error('Failed to approve clan');
    }
  };

  const declineClan = async (clanId, messageId) => {
    try {
      await axios.put(`${API}/admin/clans/${clanId}/decline`, {});
      toast.success('Clan declined');
      setMessages(messages.filter(m => m.id !== messageId));
    } catch (err) {
      toast.error('Failed to decline clan');
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'user_registration': return <Users className="w-5 h-5 text-cyan-400" />;
      case 'team_registration': return <Shield className="w-5 h-5 text-yellow-500" />;
      case 'clan_registration': return <Shield className="w-5 h-5 text-cyan-400" />;
      case 'password_reset': return <Mail className="w-5 h-5 text-orange-400" />;
      default: return <Mail className="w-5 h-5 text-zinc-400" />;
    }
  };

  if (authLoading || loading) {
    return (
      <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505]" data-testid="admin-inbox-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Inbox className="w-8 h-8 text-cyan-400" />
            <h1 className="font-heading text-3xl font-bold uppercase text-white">
              Admin Inbox
            </h1>
          </div>
          <p className="text-zinc-500">
            {messages.filter(m => m.status === 'unread').length} unread messages
          </p>
        </div>

        {/* Messages */}
        {messages.length === 0 ? (
          <div className="text-center py-24 bg-[#121212] border border-zinc-800" data-testid="no-messages">
            <MailOpen className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="font-heading text-xl text-white mb-2">No Messages</h2>
            <p className="text-zinc-500">Your inbox is empty</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`bg-[#121212] border p-6 ${message.status === 'unread' ? 'border-cyan-500/30' : 'border-zinc-800'}`}
                data-testid={`message-${message.id}`}
                onClick={() => message.status === 'unread' && markAsRead(message.id)}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    {getIcon(message.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-white truncate">{message.title}</h3>
                      {message.status === 'unread' && (
                        <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs uppercase">New</span>
                      )}
                    </div>
                    <p className="text-zinc-400 text-sm mb-4">{message.content}</p>
                    <div className="flex items-center gap-3">
                      {/* Action buttons based on message type */}
                      {message.type === 'user_registration' && message.related_id && (
                        <>
                          <Button
                            onClick={(e) => { e.stopPropagation(); approveUser(message.related_id, message.id); }}
                            className="bg-green-500 text-black hover:bg-green-400 rounded-none"
                            size="sm"
                            data-testid={`approve-user-${message.related_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve
                          </Button>
                          <Button
                            onClick={(e) => { e.stopPropagation(); declineUser(message.related_id, message.id); }}
                            variant="outline"
                            className="border-red-500/50 text-red-500 hover:bg-red-500/10 rounded-none"
                            size="sm"
                            data-testid={`decline-user-${message.related_id}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Decline
                          </Button>
                        </>
                      )}
                      {message.type === 'team_registration' && message.related_id && (
                        <>
                          <Button
                            onClick={(e) => { e.stopPropagation(); approveTeam(message.related_id, message.id); }}
                            className="bg-green-500 text-black hover:bg-green-400 rounded-none"
                            size="sm"
                            data-testid={`approve-team-${message.related_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve Team
                          </Button>
                          <Button
                            onClick={(e) => { e.stopPropagation(); declineTeam(message.related_id, message.id); }}
                            variant="outline"
                            className="border-red-500/50 text-red-500 hover:bg-red-500/10 rounded-none"
                            size="sm"
                            data-testid={`decline-team-${message.related_id}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Decline Team
                          </Button>
                        </>
                      )}
                      {message.type === 'clan_registration' && message.related_id && (
                        <>
                          <Button
                            onClick={(e) => { e.stopPropagation(); approveClan(message.related_id, message.id); }}
                            className="bg-green-500 text-black hover:bg-green-400 rounded-none"
                            size="sm"
                            data-testid={`approve-clan-${message.related_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve Clan
                          </Button>
                          <Button
                            onClick={(e) => { e.stopPropagation(); declineClan(message.related_id, message.id); }}
                            variant="outline"
                            className="border-red-500/50 text-red-500 hover:bg-red-500/10 rounded-none"
                            size="sm"
                            data-testid={`decline-clan-${message.related_id}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Decline Clan
                          </Button>
                        </>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteMessage(message.id); }}
                        className="text-zinc-500 hover:text-red-500 transition-colors ml-auto"
                        data-testid={`delete-message-${message.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="text-xs text-zinc-600 flex-shrink-0">
                    {new Date(message.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
